import { useBoolean, useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import {
  createContext,
  FC,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'
import CsvDownloader from 'react-csv-downloader'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import { actionsHeaderKpi } from '@helper/kpi/logistic/actionsHeaderKpi'
import { formatParamsModalKpiToSend } from '@helper/kpi/logistic/formatParamsModalKpiToSend'
import { paramsToSendMapping } from '@helper/kpi/logistic/paramsToSendMapping'
import { parseLogisticKpiData } from '@helper/kpi/logistic/parseLogisticKpiData'
import { parseLogisticKpiDataModal } from '@helper/kpi/logistic/parseLogisticKpiDataModal'
import { parseLogisticKpiExportData } from '@helper/kpi/logistic/parseLogisticKpiExportData'
import { validateGroupName } from '@helper/kpi/logistic/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/kpi/logistic/validateIfShowColumnInTable'
import { formattedDate } from '@helper/kpi/sales/formattedDateKpi'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import generic from '@service/generic'
import kpi from '@service/kpi'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IDefaultDate,
  IGraphicResponse,
  IKpiLogisticContext,
  IkpiModalTableStructure,
  ILineParams,
  IParamsToSend,
  IParseParamsModalOrGraphic,
  IUpdateParams,
  Pagination
} from '@base/@types/providers/Kpi/LogisticProvider'
import { logisticFieldsFilter } from '@base/variables/headers/kpi/logistic/logisticFieldsFilter'
import { logisticIndicatorsRequest } from '@base/variables/headers/kpi/logistic/logisticIndicatorsRequest'
import { logisticKpiHeader } from '@base/variables/headers/kpi/logistic/logisticKpiHeader'
import { logisticKpiModalTableStructure } from '@base/variables/headers/kpi/logistic/logisticKpiModalTableStructure'

enum DaysQuantity {
  year = 365
}

const KpiLogisticProvider: FC = ({ children }) => {
  const toast = useToast()
  const { register } = useSessions()
  const filtersList: ILayoutFilter[] = logisticFieldsFilter

  const [disabledDownload, setDisabledDownload] = useState(false)
  const [query, setQuery] = useState('')
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [isModal, setIsModal] = useState(false)
  const [isModalConfig, setIsModalConfig] = useState(false)
  const [isGraphic, setIsGraphic] = useState(false)
  const [isShowContentChart, setIsShowContentChart] = useState(false)
  const [graphicValues, setGraphicValues] = useState({} as IGraphicResponse)
  const [filters, setFilters] = useBoolean(true)
  const [loading, setLoading] = useState(false)
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [loadingGraphic, setLoadingGraphic] = useState(false)
  const [selected, setSelected] = useState(false)
  const [fetching, _setFetching] = useState(false)
  const [filename, setFilename] = useState('KPI Logística')
  const [stateLineData, setStateLineData] = useState({} as ILineParams)
  const [loadingModal, setLoadingModal] = useState(false)
  const csvDownload = useRef(null)

  const [data, updateData] = useImmer<any[]>([])
  const [modalData, updateModalData] = useImmer<any[]>([])
  const [params, updateParams] = useImmer<any>(logisticIndicatorsRequest)
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(logisticKpiHeader)
  const [modalHeaders, updateModalHeaders] = useState<
    IkpiModalTableStructure[]
  >(logisticKpiModalTableStructure)
  const [dataNew, updateDataNew] = useImmer<any[]>([])

  const [paramsGraphicToSend, setParamsGraphicToSend] =
    useState<IParamsToSend>()

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const dateEnd = dateNow.setDate(dateNow.getDate() - DaysQuantity.year)
    const initialDate = dayjs(dateEnd).format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...logisticIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }

    return paramsFormatted
  })

  const updateIsModalConfig = () => {
    setIsModalConfig(!isModalConfig)
  }

  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const getDataGraphic = useCallback(
    (paramsToSendFormatted: IParamsToSend) => {
      setIsShowContentChart(true)
      setLoadingGraphic(true)

      kpi
        .postLogisticIndicatorGraphicData(paramsToSendFormatted || paramsToSend)
        .then(({ data: { data } }) => {
          setGraphicValues(data)
          setLoadingGraphic(false)
        })
        .catch(({ response: { data } }) => {
          setIsGraphic(false)
          setLoadingGraphic(false)
          setIsShowContentChart(false)
          setGraphicValues({
            desvioMedio: []
          } as IGraphicResponse)

          const isData = data.message === 'Nenhum dado encontrado'

          toast({
            title: isData ? 'Dados não encontrados.' : data.message,
            description: 'Não há dados para que possa ser montado o gráfico',
            status: isData ? 'info' : 'error',
            duration: 4000,
            isClosable: true,
            position: 'top-right'
          })
        })
    },
    [paramsToSend, toast]
  )

  const getDataModal = useCallback(
    (paramsToSendFormatted: IParamsToSend) => {
      setParamsGraphicToSend(paramsToSendFormatted)

      kpi
        .getLogisticIndicatorsProductData(paramsToSendFormatted || paramsToSend)
        .then(({ data: { data } }) => {
          setIsModal(true)
          updateModalData(data)
          setLoadingModal(false)
        })
        .catch(() => {
          updateModalData([])
          setLoadingModal(false)
        })
    },
    [paramsToSend, updateModalData]
  )

  const parseParamsModalOrGraphic = ({
    isClickLine,
    lineData
  }: IParseParamsModalOrGraphic) => {
    if (localStorage.getItem('@kpiLogisticParamsToSend:')) {
      const paramsToSendSaved = JSON.parse(
        localStorage.getItem('@kpiLogisticParamsToSend:')
      )

      const paramsToSendFormatted = formatParamsModalKpiToSend(
        lineData || stateLineData,
        paramsToSendSaved || paramsToSend
      )

      if (!isClickLine) {
        return getDataModal(paramsToSendFormatted)
      }

      getDataGraphic(paramsToSendFormatted)
    }
  }

  const updateIsGraphic = (lineData: ILineParams) => {
    setIsGraphic(true)
    setStateLineData(lineData)

    parseParamsModalOrGraphic({
      isClickLine: true,
      lineData
    })
  }

  const handleSelected = (lineData: ILineParams) => {
    setSelected(!!lineData)
    setStateLineData(lineData)
    setLoadingModal(true)

    if (lineData) {
      parseParamsModalOrGraphic({
        isClickLine: false,
        lineData
      })
    }
  }

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: handleSelected
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tableInteractionModal = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        onFocus: true,
        icon: <AiOutlineBarChart />,
        onSelect: () => []
      }
    ]
  }, [])

  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('@kpiLogisticColumnsTable:')) {
      const newHeader = decrypterHeaderStorange('@kpiLogisticColumnsTable:')

      updateHeaders(newHeader)
    }
  }, [updateHeaders])

  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('@kpiLogisticColumnsTable:')) {
      encrypteHeaderStorange('@kpiLogisticColumnsTable:', headers)
    } else {
      localStorage.removeItem('@kpiLogisticColumnsTable:')
      encrypteHeaderStorange('@kpiLogisticColumnsTable:', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true,
        isNumeric: newHeaders[index].columns[column].isNumeric
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )

  const updateModalColumnsAndSaveState = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...modalHeaders]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateModalHeaders(newHeaders)
    },
    [modalHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return actionsHeaderKpi({
      data,
      headers,
      toggleColumn,
      parseLogisticKpiData
    })
  }, [toggleColumn, data, headers])

  const updateShowTableInTable = (data: any[]) => {
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(data)
    })

    const headersFormatted = validateIfShowColumnInTable(headers, paramsToSend)

    handleStorange()
    updateHeaders(headersFormatted)
  }

  const clearAllFilters = () => {
    const paramsFormatted = { ...logisticIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }

    updateParams({
      ...params,
      empresaIds: false,
      empresaSelectIds: [],
      centroCdLojaIds: false,
      centroCdLojaSelectIds: [],
      fornecedorSelectIds: [],
      gerenteCategoriaIds: false,
      gerenteCategoriaSelectIds: [],
      gerenteProdutoIds: false,
      gerenteProdutoSelectIds: [],
      categoriaCompactaIds: false,
      categoriaCompactaSelectIds: [],
      periodoIds: false
    })

    setParamsToSend(paramsFormatted)

    setCurrentyDate({
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
    if (localStorage.getItem('kpiLogisticParams')) {
      localStorage.setItem(
        'kpiLogisticParams',
        JSON.stringify({
          ...params,
          empresaIds: false,
          empresaSelectIds: [],
          centroCdLojaIds: false,
          centroCdLojaSelectIds: [],
          fornecedorSelectIds: [],
          gerenteCategoriaIds: false,
          gerenteCategoriaSelectIds: [],
          gerenteProdutoIds: false,
          gerenteProdutoSelectIds: [],
          categoriaCompactaIds: false,
          categoriaCompactaSelectIds: [],
          periodoIds: false
        })
      )
    }
    if (localStorage.getItem('kpiLogisticParamsRequest')) {
      localStorage.setItem(
        'kpiLogisticParamsRequest',
        JSON.stringify(paramsFormatted)
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    name === 'initialDate' &&
      setCurrentyDate({ initialDate: value, finalDate: currentyDate.finalDate })

    name === 'finalDate' &&
      setCurrentyDate({
        finalDate: value,
        initialDate: currentyDate.initialDate
      })

    const paramsToSendFormatted = paramsToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('kpiLogisticParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('kpiLogisticParams') || ''
      )

      localStorage.setItem(
        'kpiLogisticParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('kpiLogisticParamsRequest')) {
      localStorage.setItem(
        'kpiLogisticParamsRequest',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('kpiLogisticParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('kpiLogisticParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('kpiLogisticParams', JSON.stringify(params))
    }
    if (localStorage.getItem('kpiLogisticParamsRequest')) {
      const newParams = JSON.parse(
        localStorage.getItem('kpiLogisticParamsRequest') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'kpiLogisticParamsRequest',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        'kpiLogisticParamsRequest:',
        JSON.stringify(paramsToSend)
      )
      let newParams = {}
      if (localStorage.getItem('kpiLogisticParamsRequest')) {
        newParams = JSON.parse(
          localStorage.getItem('kpiLogisticParamsRequest') || ''
        )
      } else {
        newParams = paramsToSend
      }
      kpi
        .getLogisticIndicatorsData(newParams, _page)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    },
    [paramsToSend, updateData, updatePagination]
  )

  const getData = () => {
    setGraphicValues({ desvioMedio: [] })
    setIsShowContentChart(false)
    setIsGraphic(false)
    setLoading(true)
    getMaintence()

    localStorage.setItem(
      '@kpiLogisticParamsToSend:',
      JSON.stringify(paramsToSend)
    )
    try {
      localStorage.setItem(
        '@kpiLogisticParamsToSend:',
        JSON.stringify(paramsToSend)
      )
      let newParams = {}
      if (localStorage.getItem('kpiLogisticParamsRequest')) {
        newParams = JSON.parse(
          localStorage.getItem('kpiLogisticParamsRequest') || ''
        )
      } else {
        newParams = paramsToSend
      }

      kpi
        .getLogisticIndicatorsData(newParams, 1)
        .then(({ data: { data, pagination } }) => {
          updateShowTableInTable(data)
          updatePagination(pagination)

          localStorage.setItem(
            '@kpiLogisticParamsResponse:',
            JSON.stringify(data)
          )

          register({
            name: 'KPI Logística',
            path: '/kpi/logistica',
            loading: false
          })
          setFilters.toggle()
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    } catch {
      updateData([])
      setLoading(false)
    }
  }

  const exportKpiData = useCallback(() => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSendFormatted = parseLogisticKpiExportData(
      headers,
      paramsToSend
    )

    kpi
      .postLogisticIndicatorExportData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, filename + '.csv')
        Swal.close()
      })
      .catch(({ response: { data } }) => {
        const message =
          data.response?.status === 400
            ? data.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      })
  }, [filename, headers, paramsToSend, toast])

  const columns = useMemo(() => {
    const data: any[] = []

    for (const header of modalHeaders!) {
      if (!header.columns) continue

      for (const column of header.columns) {
        if (column.show) {
          data.push({
            id: column.id,
            displayName: `${header.group} - ${column.name}`
          })
        }
      }
    }

    return data
  }, [modalHeaders])

  const rows = useMemo(() => {
    const parsedData = parseLogisticKpiDataModal(dataNew)

    const body = []

    for (const row of parsedData) {
      const item: any = {}

      for (const column of columns) {
        if (column.id === 'parametros-emax') {
          if (row[column.id].includes('.')) {
            item[column.id] = Number(row[column.id].replace('.', ''))
          } else {
            item[column.id] = Number(row[column.id])
          }
        } else {
          item[column.id] = row[column.id]
        }
      }

      body.push(item)
    }

    return body
  }, [columns, dataNew])

  const exportKpiDataModal = useCallback(() => {
    setIsLoadingExport(true)

    const paramsToSendFormatted = { ...paramsGraphicToSend }

    paramsToSendFormatted.fornecedor = {
      ids: [stateLineData['fornecedor-id']],
      agrupar: paramsToSend.fornecedor?.agrupar
    }
    kpi
      .getLogisticIndicatorsProductData(paramsToSendFormatted)
      .then(({ data: { data } }) => {
        setDisabledDownload(true)
        setIsLoadingExport(false)
        updateDataNew(data)
        csvDownload.current.handleClick()
        setDisabledDownload(true)

        Swal.close()
      })
      .catch(() => {
        setIsLoadingExport(false)
        setDisabledDownload(true)
      })
  }, [
    paramsGraphicToSend,
    paramsToSend.fornecedor?.agrupar,
    stateLineData,
    updateDataNew
  ])

  const getMaintence = useCallback(async () => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        if (data.valor === 'true') {
          return (window.location.href = '/manutencao')
        }
      })
      .catch(() => {})
  }, [])

  return (
    <KpiLogisticContext.Provider
      value={{
        data: parseLogisticKpiData(data),
        query,
        params,
        isModal,
        headers,
        actions,
        filters,
        loading,
        fetching,
        filename,
        selected,
        filtersList,
        defaultDate,
        currentyDate,
        tableInteraction,
        handleLocalParams,
        getData,
        setQuery,
        setFilters,
        setFilename,
        exportKpiData,
        clearAllFilters,
        handleUpdateParams,
        pagination,
        handlePageChange,
        loadingModal,
        isModalConfig,
        isLoadingExport,
        isModalOpen: selected,
        headersModal: modalHeaders,
        actionsModal: tableInteractionModal,
        kpiModalTableStructure: modalHeaders,
        dataModal: parseLogisticKpiDataModal(modalData),
        exportKpiDataModal,
        updateIsModalConfig,
        onModalClose: () => setSelected(!selected),
        onModalChange: updateModalColumnsAndSaveState,

        isGraphic,
        graphicValues,
        loadingGraphic,
        isShowContentChart,
        setIsGraphic,
        updateIsGraphic
      }}
    >
      {children}
      {disabledDownload && (
        <CsvDownloader
          filename={'Kpi-Logistica Produtos'}
          columns={columns}
          datas={rows}
          ref={csvDownload}
          separator=";"
          wrapColumnChar='"'
          bom={true}
        ></CsvDownloader>
      )}
    </KpiLogisticContext.Provider>
  )
}

export default KpiLogisticProvider
export const KpiLogisticContext = createContext<IKpiLogisticContext>(
  {} as IKpiLogisticContext
)
